import { Link } from "react-router-dom";
import { LANDING } from "@/constants/testIds";
import { simulate, fmtMoney, fmtNumber } from "@/lib/simulator";
import {
  ChartLineUp, Stack, Coins, Shield, Sparkle, ArrowRight,
} from "@phosphor-icons/react";
import { useI18n } from "@/i18n";

// Live, honest sample scenario — actually computed by the simulator.
// Keeps landing numbers in sync with the real engine forever.
const SAMPLE_INPUTS = {
  myPackage: "gold",
  directs: 5,
  directPackage: "gold",
  l2PerDirect: 3,
  l2Package: "bronze",
  l3PerL2: 3,
  l3Package: "bronze",
  l4PerL3: 3,
  l4Package: "bronze",
  l5PerL4: 3,
  l5Package: "bronze",
  duplicationPct: 35,
  configuredLevels: 5,
  calculateDeeper: false,
};

export default function Landing() {
  const { t } = useI18n();
  const sample = simulate(SAMPLE_INPUTS);
  const teamSize = sample.tree.reduce((a, l) => a + l.members, 0);
  const orgVolume = sample.tree.reduce((a, l) => a + l.volume, 0);
  const commissionPct = orgVolume > 0 ? (sample.totals.totalCash / orgVolume) * 100 : 0;

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {/* LEFT — messaging */}
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs bg-white/[0.04] border border-white/10">
            <span className="h-1.5 w-1.5 rounded-full bg-orange-400 animate-pulse" />
            <span className="text-slate-300">
              {t("landing.eyebrow")}
            </span>
          </div>

          <h1 className="mt-6 font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            {t("landing.title.pre")}{" "}
            <span className="grad-text">{t("landing.title.highlight")}</span>{" "}
            {t("landing.title.post")}
          </h1>

          <p className="mt-6 text-slate-300 text-lg max-w-xl leading-relaxed">
            {t("landing.subtitle")}
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to="/simulator"
              data-testid={LANDING.ctaStart}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium text-sm shadow-lg shadow-orange-500/20 hover:shadow-pink-500/30 transition-shadow"
              style={{ background: "linear-gradient(90deg,#F97316,#EC4899 55%,#A855F7)" }}
            >
              {t("landing.cta.primary")}
              <ArrowRight
                weight="bold"
                className="h-4 w-4 group-hover:translate-x-0.5 transition-transform"
              />
            </Link>
            <Link
              to="/rules"
              data-testid={LANDING.ctaRules}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-slate-200 text-sm border border-white/10 hover:border-white/25 hover:bg-white/[0.03] transition-colors"
            >
              {t("landing.cta.secondary")}
            </Link>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-xl">
            <FactCard icon={Stack} label="Depth" value="20 levels" />
            <FactCard icon={Coins} label="Bonuses" value="4 engines" />
            <FactCard icon={Shield} label="Cap" value="3 payments / order" />
          </div>
        </div>

        {/* RIGHT — honest sample card */}
        <div className="lg:col-span-5 relative fade-up fade-up-2">
          <div
            className="absolute -inset-8 rounded-3xl blur-3xl opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(closest-side,#EC4899,transparent 70%)" }}
          />
          <div className="relative glass rounded-3xl p-6 md:p-7 grad-border">
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-400 uppercase tracking-widest">
                Sample scenario · exact math
              </div>
              <ChartLineUp weight="duotone" className="h-5 w-5 text-pink-400" />
            </div>

            <div className="mt-1 text-[11px] text-slate-500 leading-snug">
              Gold starter + 5 directs on Gold + 3 recruits per level down to
              L5 on Bronze. No deeper projection.
            </div>

            {/* Primary metric: You earn (dominant) — mirrors the simulator hero */}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">
                You earn
              </div>
              <div className="mt-1 font-mono font-semibold grad-text leading-none text-5xl sm:text-6xl earnings-pulse">
                {fmtMoney(sample.totals.totalCash)}
              </div>
              <div className="text-[11px] text-slate-500 mt-2">
                in commissions ({commissionPct.toFixed(2)}% of org volume)
              </div>
            </div>

            {/* Secondary metrics: team volume + shares */}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">
                  Team volume
                </div>
                <div className="mt-1 font-mono text-base font-semibold text-slate-100">
                  {fmtMoney(orgVolume)}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {fmtNumber(teamSize)} members
                </div>
              </div>
              <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">
                  Plus shares
                </div>
                <div className="mt-1 font-mono text-base font-semibold text-transparent bg-clip-text bg-gradient-to-r from-sky-300 to-cyan-200">
                  {fmtNumber(sample.shares.totalShares)}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {fmtNumber(sample.shares.startingShares)} start +{" "}
                  {fmtNumber(sample.shares.earnedShares)} DM
                </div>
              </div>
            </div>

            {/* Stream breakdown */}
            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <StreamRow
                label="Initial Ripple"
                value={fmtMoney(sample.initialRipple.total)}
                color="from-orange-500 to-orange-300"
              />
              <StreamRow
                label="Ripple Wave"
                value={fmtMoney(sample.rippleWave.total)}
                muted={sample.rippleWave.total === 0}
                color="from-pink-500 to-pink-300"
                sub={sample.rippleWave.total === 0 ? "unlocks at R6+" : undefined}
              />
              <StreamRow
                label="Deep Ripple"
                value={fmtMoney(sample.deepRipple.total)}
                muted={sample.deepRipple.total === 0}
                color="from-purple-500 to-purple-300"
                sub={sample.deepRipple.total === 0 ? "unlocks at R12+" : undefined}
              />
              <StreamRow
                label="Drop Match"
                value={`${fmtNumber(sample.dropMatch.sharesTotal)} shares`}
                color="from-sky-400 to-blue-300"
              />
            </div>

            <div className="mt-5 flex items-start gap-2 text-[11px] text-slate-500 leading-relaxed">
              <Sparkle weight="fill" className="h-3 w-3 mt-0.5 text-pink-400 flex-shrink-0" />
              <span>
                Plus {fmtNumber(sample.shares.startingShares)} starting shares
                from the Gold package itself. Numbers above come live from the
                simulator engine — reload to verify.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FactCard({ icon: Icon, label, value }) {
  return (
    <div className="glass rounded-xl p-4">
      <Icon weight="duotone" className="h-5 w-5 text-orange-400 mb-2" />
      <div className="text-[10px] uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-white font-medium">{value}</div>
    </div>
  );
}

function StreamRow({ label, value, color, sub, muted }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-3">
      <div className="text-[11px] uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div
        className={`mt-1 font-mono text-base font-semibold ${
          muted
            ? "text-slate-500"
            : `text-transparent bg-clip-text bg-gradient-to-r ${color}`
        }`}
      >
        {value}
      </div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
